// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — Payment Method Badge Component
// ─────────────────────────────────────────────────────────────────────────────
// Displays a beautifully styled, color-coded badge with an intuitive icon
// for each payment method (UPI, Cash, Bank Transfer, Cheque, Card) with full
// support for dark and light themes.
// ─────────────────────────────────────────────────────────────────────────────

import { cn } from '@/lib/utils';
import {
  QrCode,
  Banknote,
  Building2,
  FileCheck,
  CreditCard,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';

export interface PaymentMethodBadgeProps {
  method?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}

interface MethodStyle {
  label: string;
  icon: LucideIcon;
  badgeClass: string;
  iconClass: string;
}

const METHOD_STYLES: Record<string, MethodStyle> = {
  upi: {
    label: 'UPI',
    icon: QrCode,
    badgeClass:
      'bg-purple-50 text-purple-700 border-purple-200/90 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30',
    iconClass: 'text-purple-600 dark:text-purple-400',
  },
  cash: {
    label: 'Cash',
    icon: Banknote,
    badgeClass:
      'bg-emerald-50 text-emerald-700 border-emerald-200/90 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30',
    iconClass: 'text-emerald-600 dark:text-emerald-400',
  },
  bank_transfer: {
    label: 'Bank Transfer',
    icon: Building2,
    badgeClass:
      'bg-blue-50 text-blue-700 border-blue-200/90 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30',
    iconClass: 'text-blue-600 dark:text-blue-400',
  },
  cheque: {
    label: 'Cheque',
    icon: FileCheck,
    badgeClass:
      'bg-amber-50 text-amber-700 border-amber-200/90 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30',
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  card: {
    label: 'Card',
    icon: CreditCard,
    badgeClass:
      'bg-indigo-50 text-indigo-700 border-indigo-200/90 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30',
    iconClass: 'text-indigo-600 dark:text-indigo-400',
  },
  other: {
    label: 'Other',
    icon: HelpCircle,
    badgeClass:
      'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30',
    iconClass: 'text-slate-500 dark:text-slate-400',
  },
};

export function getSalePaymentMethods(sale: {
  payments?: Array<{ payment_method: string; is_void?: boolean }>;
}): string[] {
  const activePayments = (sale.payments ?? []).filter((p) => !p.is_void && p.payment_method);
  if (activePayments.length === 0) return [];
  return Array.from(new Set(activePayments.map((p) => p.payment_method.toLowerCase())));
}

export function formatPaymentMethodLabel(method: string): string {
  const norm = (method || '').toLowerCase().trim();
  if (METHOD_STYLES[norm]) return METHOD_STYLES[norm].label;
  return method.replace(/_/g, ' ').toUpperCase();
}

export default function PaymentMethodBadge({
  method,
  size = 'md',
  className,
}: PaymentMethodBadgeProps) {
  if (!method) {
    return <span className="text-muted-foreground cursor-default select-none">—</span>;
  }

  const norm = method.toLowerCase().trim();
  const config = METHOD_STYLES[norm] || {
    label: formatPaymentMethodLabel(method),
    icon: HelpCircle,
    badgeClass:
      'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/30',
    iconClass: 'text-slate-500 dark:text-slate-400',
  };

  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-semibold select-none cursor-default shadow-2xs transition-all',
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs',
        config.badgeClass,
        className
      )}
      title={`Payment Method: ${config.label}`}
    >
      <Icon className={cn(size === 'sm' ? 'h-3 w-3 shrink-0' : 'h-3.5 w-3.5 shrink-0', config.iconClass)} />
      <span>{config.label}</span>
    </span>
  );
}
